import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ArrowLeft, 
  GripVertical, 
  RotateCcw, 
  Save, 
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Layers
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import EducatorLemonBotPanel from '@/components/EducatorLemonBotPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ClassSettingsPage({ user, onLogout }) {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [classInfo, setClassInfo] = useState(null);
  const [curriculumOrder, setCurriculumOrder] = useState(null);
  const [moduleDetails, setModuleDetails] = useState({});
  const [lessonDetails, setLessonDetails] = useState({});
  const [expandedModules, setExpandedModules] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch class info
      const classRes = await axios.get(`${API}/classes/${classId}`, { headers });
      setClassInfo(classRes.data);

      // Fetch curriculum order
      const orderRes = await axios.get(`${API}/classes/${classId}/curriculum-order`, { headers });
      setCurriculumOrder(orderRes.data);

      // Fetch module details
      const modulesRes = await axios.get(`${API}/modules`, { headers });
      const moduleMap = {};
      modulesRes.data.forEach(m => {
        moduleMap[m.id] = m;
      });
      setModuleDetails(moduleMap);

      // Fetch lesson details for each module
      const lessonMap = {};
      for (const module of modulesRes.data) {
        const lessonsRes = await axios.get(`${API}/modules/${module.id}/lessons`, { headers });
        lessonsRes.data.forEach(l => {
          lessonMap[l.id] = l;
        });
      }
      setLessonDetails(lessonMap);

    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleModuleDragStart = (e, moduleId) => {
    setDraggedItem({ type: 'module', id: moduleId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleModuleDragOver = (e, moduleId) => {
    e.preventDefault();
    if (draggedItem?.type === 'module' && draggedItem.id !== moduleId) {
      setDragOverItem({ type: 'module', id: moduleId });
    }
  };

  const handleModuleDrop = (e, targetModuleId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'module') return;

    const currentModules = [...curriculumOrder.current_order.modules];
    const dragIndex = currentModules.indexOf(draggedItem.id);
    const dropIndex = currentModules.indexOf(targetModuleId);

    if (dragIndex !== -1 && dropIndex !== -1) {
      currentModules.splice(dragIndex, 1);
      currentModules.splice(dropIndex, 0, draggedItem.id);

      setCurriculumOrder(prev => ({
        ...prev,
        current_order: {
          ...prev.current_order,
          modules: currentModules
        }
      }));
      setHasChanges(true);
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleLessonDragStart = (e, moduleId, lessonId) => {
    setDraggedItem({ type: 'lesson', moduleId, id: lessonId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleLessonDragOver = (e, moduleId, lessonId) => {
    e.preventDefault();
    if (draggedItem?.type === 'lesson' && 
        draggedItem.moduleId === moduleId && 
        draggedItem.id !== lessonId) {
      setDragOverItem({ type: 'lesson', moduleId, id: lessonId });
    }
  };

  const handleLessonDrop = (e, moduleId, targetLessonId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem.type !== 'lesson' || draggedItem.moduleId !== moduleId) return;

    const currentLessons = [...(curriculumOrder.current_order.lessons[moduleId] || [])];
    const dragIndex = currentLessons.indexOf(draggedItem.id);
    const dropIndex = currentLessons.indexOf(targetLessonId);

    if (dragIndex !== -1 && dropIndex !== -1) {
      currentLessons.splice(dragIndex, 1);
      currentLessons.splice(dropIndex, 0, draggedItem.id);

      setCurriculumOrder(prev => ({
        ...prev,
        current_order: {
          ...prev.current_order,
          lessons: {
            ...prev.current_order.lessons,
            [moduleId]: currentLessons
          }
        }
      }));
      setHasChanges(true);
    }

    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const toggleModuleExpand = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setWarnings([]);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/classes/${classId}/curriculum-order`,
        {
          modules: curriculumOrder.current_order.modules,
          lessons: curriculumOrder.current_order.lessons
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.warnings?.length > 0) {
        setWarnings(response.data.warnings);
      }
      setHasChanges(false);
      
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save curriculum order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset curriculum to default order? This cannot be undone.')) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/classes/${classId}/curriculum-order`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWarnings([]);
      setHasChanges(false);
      await fetchData();
    } catch (error) {
      console.error('Failed to reset:', error);
      alert('Failed to reset curriculum order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/educator')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.backToDashboard', 'Back to Dashboard')}
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('classSettings.title', 'Class Settings')}
              </h1>
              <p className="text-gray-600 mt-1">
                {classInfo?.name}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {curriculumOrder?.has_custom_order && (
                <button
                  onClick={handleReset}
                  disabled={saving}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                  data-testid="reset-order-btn"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('classSettings.resetToDefault', 'Reset to Default')}
                </button>
              )}
              
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  hasChanges 
                    ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
                data-testid="save-order-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')}
              </button>
            </div>
          </div>
        </div>

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-800">
                  {t('classSettings.progressionWarnings', 'Progression Warnings')}
                </h3>
                <ul className="mt-2 space-y-1">
                  {warnings.map((warning, idx) => (
                    <li key={idx} className="text-sm text-yellow-700">• {warning}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* LemonBot Panel — toggle, usage, common questions, pitch readiness, CSV */}
        <div className="mb-6">
          <EducatorLemonBotPanel classId={classId} />
        </div>

        {/* Instructions */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Layers className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-800">
                {t('classSettings.customizeCurriculum', 'Customize Curriculum Order')}
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                {t('classSettings.dragInstructions', 'Drag and drop sessions and lessons to reorder them. Students in this class will see the curriculum in your custom order.')}
              </p>
            </div>
          </div>
        </div>

        {/* Custom Order Indicator */}
        {curriculumOrder?.has_custom_order && (
          <div className="mb-4 text-sm text-gray-500 flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
            {t('classSettings.customOrderActive', 'Custom order active')}
            {curriculumOrder.current_order.updated_at && (
              <span>
                • {t('classSettings.lastUpdated', 'Last updated')}: {new Date(curriculumOrder.current_order.updated_at).toLocaleDateString()}
              </span>
            )}
          </div>
        )}

        {/* Module List */}
        <div className="space-y-3" data-testid="curriculum-order-list">
          {curriculumOrder?.current_order?.modules?.map((moduleId, moduleIndex) => {
            const module = moduleDetails[moduleId];
            if (!module) return null;

            const isExpanded = expandedModules[moduleId];
            const lessons = curriculumOrder.current_order.lessons[moduleId] || [];
            const isDragOver = dragOverItem?.type === 'module' && dragOverItem?.id === moduleId;

            return (
              <div key={moduleId} className="bg-white rounded-xl shadow-sm overflow-hidden">
                {/* Module Header */}
                <div
                  draggable
                  onDragStart={(e) => handleModuleDragStart(e, moduleId)}
                  onDragOver={(e) => handleModuleDragOver(e, moduleId)}
                  onDrop={(e) => handleModuleDrop(e, moduleId)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 p-4 cursor-move transition-colors ${
                    isDragOver ? 'bg-yellow-100 border-2 border-yellow-400 border-dashed' : 'hover:bg-gray-50'
                  }`}
                  data-testid={`module-item-${moduleId}`}
                >
                  <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 font-bold text-sm">
                    {moduleIndex + 1}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {module.title}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {lessons.length} {t('classSettings.lessons', 'lessons')}
                    </p>
                  </div>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleModuleExpand(moduleId);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Lessons (expandable) */}
                {isExpanded && lessons.length > 0 && (
                  <div className="border-t border-gray-100 bg-gray-50 p-3 space-y-2">
                    {lessons.map((lessonId, lessonIndex) => {
                      const lesson = lessonDetails[lessonId];
                      if (!lesson) return null;

                      const isLessonDragOver = dragOverItem?.type === 'lesson' && 
                                               dragOverItem?.moduleId === moduleId && 
                                               dragOverItem?.id === lessonId;

                      return (
                        <div
                          key={lessonId}
                          draggable
                          onDragStart={(e) => handleLessonDragStart(e, moduleId, lessonId)}
                          onDragOver={(e) => handleLessonDragOver(e, moduleId, lessonId)}
                          onDrop={(e) => handleLessonDrop(e, moduleId, lessonId)}
                          onDragEnd={handleDragEnd}
                          className={`flex items-center gap-3 p-3 bg-white rounded-lg cursor-move transition-colors ${
                            isLessonDragOver ? 'ring-2 ring-yellow-400 ring-dashed' : 'hover:bg-gray-50'
                          }`}
                          data-testid={`lesson-item-${lessonId}`}
                        >
                          <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                          
                          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 font-medium text-xs">
                            {lessonIndex + 1}
                          </div>
                          
                          <BookOpen className="w-4 h-4 text-gray-400" />
                          
                          <span className="flex-1 text-sm text-gray-700 truncate">
                            {lesson.title}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Save reminder */}
        {hasChanges && (
          <div className="mt-6 p-4 bg-yellow-100 border border-yellow-300 rounded-xl text-center">
            <p className="text-yellow-800 font-medium">
              {t('classSettings.unsavedChanges', 'You have unsaved changes. Don\'t forget to save!')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClassSettingsPage;
